import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useLanguage } from '../contexts/LanguageContext';

const Section = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Title = styled.h2`
  color: white;
  text-align: center;
  font-size: 3rem;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
`;

const Card = styled(Link)`
  position: relative;
  display: block;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  text-decoration: none;
  background-color: #47ABDB;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem;
  background: linear-gradient(to top, rgba(6, 13, 37, 0.85), rgba(6, 13, 37, 0));
`;

const CardTitle = styled.p`
  color: white;
  font-weight: bold;
  font-size: 1.1rem;
  margin: 0;
`;

const CardDate = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  margin: 0.2rem 0 0;
`;

const PastEventsGallery = () => {
  const [events, setEvents] = useState([]);
  const { language } = useLanguage();

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return;
    axios.get(`${apiUrl}/api/cms/event/public/get-past`)
      .then((response) => setEvents(response.data || []))
      .catch(() => setEvents([]));
  }, []);

  if (events.length === 0) {
    return null;
  }

  const getContent = (defaultContent, enContent) => {
    return language === 'en' && enContent ? enContent : (defaultContent || '');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <Section>
      <Title>
        <FormattedMessage id="pastEvents.title" defaultMessage="PAST HEARTS EVENTS" />
      </Title>
      <Grid>
        {events.filter((event) => event.slug).map((event) => {
          // No hardcoded placeholder here on purpose - an event with no
          // headline entered in the CMS yet should show no title, not
          // invented copy (same fix as EventDetail.js).
          const displayName = getContent(event.mainHeadline, event.mainHeadlineEN)
            || getContent(event.bigHeadline, event.bigHeadlineEN)
            || '';
          return (
            <Card key={event.id} to={`/events/${event.slug}`}>
              {event.mainMediaUrl && (
                <CardImage
                  src={`${process.env.REACT_APP_API_URL || ''}${event.mainMediaUrl}`}
                  alt={displayName}
                />
              )}
              <CardOverlay>
                {displayName && <CardTitle>{displayName}</CardTitle>}
                {event.dateTime && <CardDate>{formatDate(event.dateTime)}</CardDate>}
              </CardOverlay>
            </Card>
          );
        })}
      </Grid>
    </Section>
  );
};

export default PastEventsGallery;
